import { h, type Child } from "./safe";

// Small view blocks built on the EXISTING app styles (style.css: .pill, .tag, .empty, .error-banner, .card)
// plus a few qf-* additions from queueFleet.css. No prototype shell, no .cp-* classes.

export type PillTone = "ok" | "warn" | "bad" | "neutral";

export function pill(text: string, tone: PillTone = "neutral"): HTMLSpanElement {
  return h("span", { class: `pill ${tone}` }, text);
}

export function tag(text: string): HTMLSpanElement {
  return h("span", { class: "tag" }, text);
}

/** A failure scoped to one region. `retry` re-runs the failed load and keeps the person's filters. */
export function errorBanner(message: string, opts: { retry?: () => void; label?: string } = {}): HTMLElement {
  return h(
    "div",
    { class: "error-banner qf-error", role: "alert" },
    h("span", { class: "qf-error__text" }, message),
    opts.retry ? h("button", { type: "button", class: "secondary", on: { click: opts.retry } }, opts.label ?? "Try again") : null,
  );
}

export function emptyBlock(title: string, body?: Child, actions: Child[] = []): HTMLElement {
  return h("div", { class: "empty qf-empty" }, h("strong", null, title), body ? h("p", null, body) : null, actions.length ? h("div", { class: "btn-group qf-empty__actions" }, ...actions) : null);
}

/** Placeholder rows that keep the layout steady while data loads. */
export function loadingBlock(label: string, rows = 5): HTMLElement {
  const widths = ["94%", "82%", "88%", "70%", "84%"];
  return h(
    "div",
    { class: "qf-loading", role: "status", "aria-busy": "true", "aria-label": label },
    ...Array.from({ length: rows }, (_, i) => h("span", { class: "qf-skeleton", css: { width: widths[i % widths.length] } })),
    h("span", { class: "qf-sr-only" }, `${label}…`),
  );
}

/** Scroll wrapper that can be scrolled with the keyboard and is named for assistive technology. */
export function tableRegion(label: string, table: HTMLTableElement): HTMLElement {
  return h("div", { class: "qf-table-wrap", role: "region", "aria-label": label, tabindex: "0" }, table);
}
